<template>
  <div
    id="allPostLogin"
    class="modal fade"
    data-bs-backdrop="static"
    style="zoom: 80%"
  >
    <ModalDialog size="lg">
      <div
        @click="currentIndex > 0 ? currentIndex-- : null"
        class="nav-circle left-nav d-flex align-items-center justify-content-center"
        :class="{ 'v-hide': currentIndex === 0 }"
      >
        <i class="bi bi-chevron-left"></i>
      </div>
      <Transition name="fade" mode="out-in">
        <ModalContent :key="currentIndex" style="border: none">
          <div>
            <div class="position-relative">
              <button
                type="button"
                class="btn-close custom-btn-close"
                aria-label="Close"
                style="position: absolute; right: 40px; top: 30px; z-index: 1"
                v-if="canCloseModal && (!openedWithUnseen || isLastPage)"
                @click="handleClose"
              >
                <img
                  src="@/assets/svg/white-outline-close.svg"
                  alt="Close"
                  width="30"
                  height="30"
                  style="max-width: unset"
                />
              </button>
              <div class="mb-4">
                <img
                  :src="postLoginData?.imageUrl"
                  style="
                    width: 100%;
                    border-radius: 10px 10px 0 0;
                    height: auto;
                    max-height: 500px;
                  "
                />
              </div>
              <h2 v-if="postLoginData?.headline" class="pb-4 px-5 text-center">
                {{ postLoginData?.headline }}
              </h2>
              <div style="padding: 0 2rem">
                <div class="card subtle-shadow">
                  <div class="card-body">
                    <p
                      v-if="postLoginData?.description"
                      class="px-4 mt-1 mb-3"
                      style="color: rgb(81, 96, 105); overflow-wrap: break-word"
                    >
                      {{ postLoginData?.description }}
                      <a
                        v-if="
                          postLoginData?.descriptionLinkUrl &&
                          postLoginData?.descriptionLinkText
                        "
                        :href="formatUrl(postLoginData?.descriptionLinkUrl)"
                        target="_blank"
                        >{{ postLoginData?.descriptionLinkText }}</a
                      >
                    </p>
                    <ul class="px-5">
                      <template v-for="(item, _index) in content" :key="_index">
                        <div
                          class="p-0 position-relative item-container"
                          v-if="
                            item.enabled === true &&
                            item.title &&
                            item.description
                          "
                        >
                          <li style="color: rgb(81, 96, 105)">
                            <h4 class="mb-0">
                              {{ item.title }}
                            </h4>
                            <p style="line-height: 1.5rem">
                              {{ item.description }}
                              <a
                                :href="formatUrl(item.hyperlinkUrl)"
                                target="_blank"
                                v-if="item.hyperlinkText && item.hyperlinkUrl"
                                >{{ item.hyperlinkText }}</a
                              >
                            </p>
                          </li>
                        </div>
                      </template>
                    </ul>
                  </div>
                </div>
              </div>
              <div class="mt-5 px-5 mb-2 d-flex justify-content-end">
                <Button
                  class="btn btn-light"
                  style="
                    background-color: white;
                    border-color: gray;
                    font-weight: 500;
                  "
                  @click="
                    handleSecondaryBtnClicked(postLoginData?.secondaryButtonUrl)
                  "
                >
                  {{ postLoginData?.secondaryButtonName }}
                </Button>
                <Button
                  class="btn btn-primary ms-2"
                  @click="handlePrimaryBtnClicked(postLoginData?.mainButtonUrl)"
                >
                  {{ postLoginData?.mainButtonName }}
                </Button>
              </div>
            </div>
            <div
              v-if="postLoginStore.postLoginAnnouncementData.length > 1"
              class="mt-2"
              style="padding: 2rem 0; border-top: 1px solid #d0d0d0"
            >
              <div class="d-flex align-items-center px-5 position-relative">
                <span
                  v-if="currentIndex > 0"
                  role="button"
                  class="pager-text"
                  @click="currentIndex -= 1"
                  >&larr; Back</span
                >

                <div
                  class="d-flex justify-content-center align-items-center gap-2 position-absolute top-50 start-50 translate-middle"
                >
                  <span
                    v-for="(
                      item, i
                    ) in postLoginStore.postLoginAnnouncementData"
                    :key="item.id"
                    @click="currentIndex = i"
                    role="button"
                    :class="
                      currentIndex === i ? 'rounded-pill' : 'rounded-circle'
                    "
                    :style="{
                      width: currentIndex === i ? '20px' : '10px',
                      height: '10px',
                      backgroundColor:
                        currentIndex === i
                          ? '#ff0000'
                          : i < currentIndex
                          ? '#ffb8b8'
                          : '#c4c4c4',
                      transition: 'width 0.2s, background-color 0.2s',
                      display: 'inline-block',
                    }"
                  ></span>
                </div>

                <span
                  v-if="
                    currentIndex <
                    postLoginStore.postLoginAnnouncementData.length - 1
                  "
                  role="button"
                  class="pager-text red-link ms-auto"
                  @click="currentIndex += 1"
                  >Next &rarr;</span
                >
                <span
                  v-else
                  role="button"
                  class="pager-text red-link ms-auto"
                  @click="handleDone"
                  >Done</span
                >
              </div>
            </div>
          </div>
        </ModalContent>
      </Transition>
      <div
        @click="
          currentIndex < postLoginStore.postLoginAnnouncementData.length - 1
            ? currentIndex++
            : null
        "
        class="nav-circle right-nav d-flex align-items-center justify-content-center"
        :class="{
          'v-hide':
            currentIndex ===
            postLoginStore.postLoginAnnouncementData.length - 1,
        }"
      >
        <i class="bi bi-chevron-right"></i>
      </div>
    </ModalDialog>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";

import ModalDialog from "@/components/uielements/modal/ModalDialog.vue";
import ModalContent from "@/components/uielements/modal/ModalContent.vue";
import Button from "@/components/uielements/button/Button.vue";
import { usePostLoginAnnouncement } from "@/stores/postLoginAnnouncement.js";
import { convertToLocal } from "@/constants/dateUtils.js";

const postLoginStore = usePostLoginAnnouncement();
const emit = defineEmits(["second-cta", "main-cta", "seen", "done"]);

const currentIndex = ref(0);
const openedWithUnseen = ref(false);

const postLoginData = computed(() => {
  return postLoginStore.postLoginAnnouncementData[currentIndex.value] || null;
});

const isLastPage = computed(() => {
  return (
    currentIndex.value === postLoginStore.postLoginAnnouncementData.length - 1
  );
});

watch(currentIndex, () => {
  postLoginStore.popupTimestamp = convertToLocal(new Date().toISOString());
  if (postLoginData.value && postLoginData.value.seen === false) {
    emit("seen", postLoginData.value.id);
  }
});

watch(
  () => postLoginStore.allPostLoginModal,
  (modal) => {
    if (modal) {
      const modalElement = document.getElementById("allPostLogin");

      modalElement.addEventListener("shown.bs.modal", () => {
        openedWithUnseen.value = postLoginStore.hasNotSeenAnnouncement;

        postLoginStore.popupTimestamp = convertToLocal(
          new Date().toISOString()
        );
        if (postLoginData.value && postLoginData.value.seen === false) {
          emit("seen", postLoginData.value.id);
        }
      });
    }
  },
  { immediate: true }
);

const formatUrl = (url) => {
  if (!/^https?:\/\//i.test(url)) {
    return "https://" + url;
  }
  return url;
};

const handlePrimaryBtnClicked = (url) => {
  postLoginStore.allPostLoginModal.hide();

  emit("main-cta", postLoginData.value.id);
  window.location.href = formatUrl(url);
};

const handleSecondaryBtnClicked = (url) => {
  postLoginStore.allPostLoginModal.hide();

  if (url === "") {
    emit("second-cta", postLoginData.value.id);
  } else {
    emit("second-cta", postLoginData.value.id);
    window.location.href = formatUrl(url);
  }
};

const handleDone = () => {
  postLoginStore.sendModalToBell("allPostLogin", () => {
    postLoginStore.allPostLoginModal.hide();
    if (openedWithUnseen.value) {
      postLoginStore.triggerBellCallout();
    }
  });
  emit("done");
};

const handleClose = () => {
  postLoginStore.sendModalToBell("allPostLogin", () => {
    postLoginStore.allPostLoginModal.hide();
    if (openedWithUnseen.value) {
      postLoginStore.triggerBellCallout();
    }
  });
};

const content = computed(() => {
  return (
    postLoginData.value?.content?.map((data) => {
      return { ...data };
    }) || []
  );
});

const canCloseModal = computed(() => {
  const data = postLoginStore.postLoginAnnouncementData;
  const hasUnread = data.some((item) => !item.seen);

  if (data.length > 1 && hasUnread) {
    return false;
  }

  if (data.length > 1 && !hasUnread) {
    return true;
  }

  const lastAnnouncement = data[data.length - 1];
  return lastAnnouncement?.enabledClose === true;
});
</script>

<style scoped>
.item-container {
  position: relative;
  margin-bottom: 1.5rem;
}
.btn-close.custom-btn-close {
  background: none;
}

.nav-circle {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 50px;
  height: 50px;
  background-color: white;
  border-radius: 50%;
  border: 1px solid #dee2e6;
  cursor: pointer;
  z-index: 1060;
  transition: all 0.2s ease;
  pointer-events: auto;
}

.nav-circle:hover {
  background-color: #f8f9fa;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.left-nav {
  left: -70px;
}

.right-nav {
  right: -70px;
}

.v-hide {
  visibility: hidden;
  opacity: 0;
}

.pager-text {
  font-weight: 500;
  user-select: none;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
